import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
	Head,
	RadioButtons,
	SkeletonTemplate,
	Wrapper,
	ResetDataModal,
	TollClassesInfo,
	IHelpContent,
	renderErrorMessage,
	getDrawerHelperData,
} from '@adac-mp/components';
import {
	getVignetteUniqueCategories,
	IVehicleCategory,
	OfferContext,
	VignetteContext,
	formatPlates,
	ITypeVignetteOrderingProcessFields,
	getLabelValue,
	getExistingUniqueVehicleData,
} from '@adac-mp/shared';
import { useViewport } from '@adac-mp/components/src/hooks/useViewport';
import { useFlowId } from '@adac-mp/components/src/hooks/useFlowId';
import { withTracking } from '@adac-mp/components/src/utils/tracking/tracking';
import { renderCategoryCard } from '../../utils/renderCategoryCard';
import { getVehicleCategoriesAdditionalProps } from '../../utils/vehicle-category';

interface ICategorySelectionProps {
	reuse?: boolean;
	hasError?: boolean;
	stepCount?: number;
	helpContent?: IHelpContent;
	updateStepCount?: (stepCount: number) => void;
	updateValidatedStepsMap?: (hasError: boolean, reset?: boolean) => void;
}

// eslint-disable-next-line complexity
const CategorySelectionComponent = ({
	reuse,
	hasError,
	helpContent,
	stepCount = 0,
	updateStepCount = () => {},
	updateValidatedStepsMap = () => {},
}: ICategorySelectionProps) => {
	const [, setSearchParams] = useSearchParams();
	const flowId = Number(useFlowId());
	const { viewport } = useViewport();
	const { vignetteOffers, labels } = useContext(OfferContext);
	const {
		cartItems,
		vehicleCategory,
		contentfulLabels,
		deleteSelectedProducts,
		updateVehicleCategory,
		updatePlates,
	} = useContext(VignetteContext);
	const currentLabels = contentfulLabels as ITypeVignetteOrderingProcessFields;
	const [categories, setCategories] = useState<IVehicleCategory[] | undefined>(undefined);
	const [categoryId, setCategoryId] = useState<string | undefined>(undefined);
	const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
	const [tempCategoryId, setTempCategoryId] = useState<string | undefined>(undefined);
	const isOneCartFlow = flowId === 0;
	const hasCategoryDescription = flowId === 7 || isOneCartFlow;

	const updateCategoryUrl = (id: string) => {
		setSearchParams(
			(params) => {
				params.set('category', id);
				return params;
			},
			{ preventScrollReset: true, replace: true },
		);
	};

	const categorySelectionHandler = (selectedCategoryId?: string, vehicleCategories?: IVehicleCategory[]) => {
		setCategoryId(selectedCategoryId);
		const selectedCategory = vehicleCategories?.find(
			({ id, licenseplate }: IVehicleCategory) =>
				id === Number(selectedCategoryId?.charAt(0)) &&
				(!licenseplate || licenseplate === selectedCategoryId?.substring(2)),
		);
		if (selectedCategory) {
			updateVehicleCategory(selectedCategory);
		}
		if (selectedCategoryId && !reuse) {
			updateCategoryUrl(selectedCategoryId);
		}
		if (reuse) {
			const correspondingCartItem = cartItems.find(
				(item) =>
					item.licenseplate === selectedCategory?.licenseplate &&
					item.offer.moduleDetailId === Number(selectedCategoryId?.charAt(0)),
			);
			if (correspondingCartItem) {
				updatePlates(
					formatPlates(correspondingCartItem.licenseplate).split('-'),
					correspondingCartItem?.countryCode,
					correspondingCartItem.licenseplateType,
				);
			}
		}

		if (selectedCategory) {
			updateVehicleCategory(selectedCategory);
		}
	};

	const onClose = () => {
		setTempCategoryId(undefined);
		setModalIsOpen(false);
	};

	const onSubmit = () => {
		categorySelectionHandler(tempCategoryId, categories);
		deleteSelectedProducts();
		updateStepCount(0);
		updateValidatedStepsMap(true, true);
		onClose();
	};

	const getUrlPreselectedCountries = () => {
		const urlParams = new URLSearchParams(location.search);
		return urlParams.get('category') ?? '';
	};

	useEffect(() => {
		if (reuse) {
			const vignetteCategories = getExistingUniqueVehicleData(cartItems, isOneCartFlow).map((category) => ({
				...getVehicleCategoriesAdditionalProps(category.id, flowId, currentLabels?.categoryClasses),
				...category,
			}));
			setCategories(vignetteCategories);
		} else {
			let vignetteCategories = getVignetteUniqueCategories(vignetteOffers).map((category) => ({
				...getVehicleCategoriesAdditionalProps(category.id, flowId, currentLabels?.categoryClasses),
				...category,
			}));
			const motocycleIndex = vignetteCategories.findIndex(({ id }) => id === 3);
			vignetteCategories = vignetteCategories.concat(vignetteCategories.splice(motocycleIndex, 1));
			setCategories(vignetteCategories);
		}
	}, [vignetteOffers, cartItems, reuse, currentLabels]);

	useEffect(() => {
		if (!reuse) {
			const urlPreSelectedCategories = getUrlPreselectedCountries();
			const defaultCategory = categories?.find(({ id }) => id === 2)?.id;
			const preSelectedCategory = categories?.find(({ id }) => urlPreSelectedCategories.includes(String(id)));
			if (preSelectedCategory) {
				setCategoryId(String(preSelectedCategory.id));
				updateVehicleCategory(preSelectedCategory);
			} else if (defaultCategory) {
				setCategoryId(String(defaultCategory));
				const selectedCategory = categories?.find(({ id }: IVehicleCategory) => id === Number(defaultCategory));
				updateVehicleCategory(selectedCategory);
			}
		} else if (categories) {
			const selectedCategory = categories[0];
			const id = selectedCategory?.licenseplate
				? `${String(selectedCategory?.id)}-${selectedCategory?.licenseplate}`
				: String(selectedCategory?.id);
			setCategoryId(String(id));
			updateVehicleCategory(selectedCategory);
		}
	}, [categories, reuse]);

	useEffect(() => {
		updateValidatedStepsMap(Boolean(categoryId));
		if (!categoryId && vehicleCategory) {
			setCategoryId(String(vehicleCategory.id));
		}
	}, [categoryId, vehicleCategory]);

	const renderHeadComponent = () => (
		<Head
			headline={`${
				reuse
					? currentLabels?.tollCategoryReuseHeadline ||
					  'Für welches Fahrzeug benötigen Sie das weitere Produkt?'
					: currentLabels?.tollCategoriesHeadline || 'Mit welchem Fahrzeug sind Sie unterwegs?'
			}`}
			helpContent={helpContent}
		/>
	);

	return (
		<>
			<Wrapper animateFirstStep paddingTop="large">
				{renderHeadComponent()}
				{categories && categories.length > 0 ? (
					<>
						<div
							className={`text-center ${
								categories.length <= 2 ? 'md:w-6/12' : 'md:w-10/12'
							} md:flex md:flex-col md:self-center`}>
							{hasError &&
								renderErrorMessage({
									message: currentLabels?.errorFahrzeugauswahl || 'Bitte wählen Sie Ihr Fahrzeug aus',
									className: `${hasCategoryDescription ? 'mb-6' : 'mb-5'}`,
								})}
							<RadioButtons
								className={`relative px-2 rounded bg-white border-b-gray-600 flex-col-reverse items-center justify-between gap-4 pt-3.5 pb-5 ${
									categories.length < 2
										? 'w-full md:basis-[calc(50%_-_4px)]'
										: 'basis-[calc(50%_-_4px)]'
								}`}
								labelClassName="w-full pr-2"
								wrapperClassName={`w-full ${
									categories.length > 2 ? 'grid grid-cols-2 md:grid-cols-3' : 'flex justify-center'
								} gap-x-1.5 gap-y-3 md:gap-5 ${hasCategoryDescription && 'mb-4'}`}
								size="md"
								options={categories.map((category) => {
									const id = category.licenseplate
										? `${String(category.id)}-${category.licenseplate}`
										: String(category.id);
									return {
										id,
										label: renderCategoryCard(
											getLabelValue('mp.informationMautklasse', labels) ||
												'Information Mautklasse',
											flowId,
											categoryId,
											viewport,
											category,
										),
										title: category.name,
									};
								})}
								value={categoryId}
								setValue={(selectedCategoryId) => {
									if (stepCount === 0) {
										categorySelectionHandler(selectedCategoryId, categories);
										return;
									}
									if (categoryId !== selectedCategoryId) {
										if (!reuse) {
											updateCategoryUrl(selectedCategoryId);
										}
										setTempCategoryId(selectedCategoryId);
										setModalIsOpen(true);
									}
								}}
							/>
							{hasCategoryDescription && (
								<TollClassesInfo
									reuse={reuse}
									tollClassLabel={getLabelValue('mp.mautklasse', labels) || 'Information Mautklasse'}
									linkText={currentLabels?.categoryClass2BList}
									helperDrawer={getDrawerHelperData(
										flowId === 7 ? 'Fahrzeuge_Mautklasse_2B' : 'vehicleCategories',
										currentLabels?.helpDrawers,
									)}
								/>
							)}
						</div>
					</>
				) : (
					<SkeletonTemplate
						rowNumber={flowId === 3 ? 2 : 3}
						type="card"
						hasDescription={hasCategoryDescription}
					/>
				)}

				<ResetDataModal isOpen={modalIsOpen} onClose={onClose} onSubmit={onSubmit} />
			</Wrapper>
		</>
	);
};

export const CategorySelection = withTracking(CategorySelectionComponent);
